const CKO_SCRIPT_URL = 'https://checkout-web-components.checkout.com/index.js'
const CKO_SCRIPT_ID = 'checkout-web-components'

export async function ensureCheckoutWebComponents() {
  if (typeof window === 'undefined') {
    return
  }
  if (window.CheckoutWebComponents) {
    return
  }

  const existing = document.getElementById(CKO_SCRIPT_ID)
  if (!existing) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.id = CKO_SCRIPT_ID
      script.src = CKO_SCRIPT_URL
      script.async = false
      script.onload = () => {
        script.setAttribute('data-loaded', 'true')
        resolve()
      }
      script.onerror = () => reject(new Error('Checkout Web Components failed to load'))
      document.head.appendChild(script)
    })
  }

  let retries = 0
  const maxRetries = 100
  while (!window.CheckoutWebComponents && retries < maxRetries) {
    await new Promise(resolve => setTimeout(resolve, 100))
    retries++
  }

  if (!window.CheckoutWebComponents) {
    throw new Error('Checkout Web Components failed to load')
  }
}

/**
 * Waits for a container element to appear in the DOM.
 * Returns true once it is present, or false if it never appears within maxWaitMs.
 * Never throws: a missing container means the CKO Flow UI isn't on screen, in which
 * case the caller should abort mounting rather than surface an error.
 */
export async function waitForCheckoutContainer(containerId, maxWaitMs = 10000) {
  if (typeof document === 'undefined') {
    return false
  }

  const deadline = Date.now() + maxWaitMs
  while (!document.getElementById(containerId)) {
    if (Date.now() > deadline) {
      return false
    }
    await new Promise(resolve => setTimeout(resolve, 100))
  }
  return true
}

/**
 * Initializes Checkout Web Components and mounts the Flow component.
 * @param {object} params
 * @returns {Promise<{ checkout: object, flowComponent: object, addressComponent?: object }>}
 */
export async function initializeCheckout(params) {
  const {
    paymentSession,
    environment = 'sandbox',
    containerId = 'flow-container',
    addressContainerId,
    appearance,
    componentOptions,
    handleSubmit,
    flowComponentOptions,
    createAddressComponent,
  } = params

  const publicKey = process.env.CKO_PUBLIC_KEY

  if (!publicKey) {
    throw new Error('CKO_PUBLIC_KEY environment variable is not set')
  }

  await ensureCheckoutWebComponents()

  // Confirm the mount point exists BEFORE instantiating CheckoutWebComponents.
  // The SDK writes `environment` into a global config that becomes immutable
  // after the first instantiation and lives for the whole browser session. If we
  // instantiated first and then bailed because the container wasn't ready, the
  // caller's retry would call CheckoutWebComponents a second time and crash with
  // "Can not update runtime configuration property: environment". Waiting here
  // guarantees CheckoutWebComponents is only ever instantiated on an attempt that
  // can actually mount, so retries never produce a second instantiation.
  const containerReady = await waitForCheckoutContainer(containerId)
  if (!containerReady) {
    // The mount point isn't on screen (e.g. user navigated away or is on a flow
    // that doesn't render the CKO Flow drop-in). Abort instead of throwing.
    console.warn(
      `CKO Flow container "${containerId}" not present; skipping mount`,
    )
    return null
  }

  const checkout = await window.CheckoutWebComponents({
    paymentSession,
    publicKey,
    environment,
    ...(appearance && { appearance }),
    ...(componentOptions && { componentOptions }),
    ...(handleSubmit && { handleSubmit }),
  })

  const flowComponent = checkout.create('flow', {
    showPayButton: false,
    ...flowComponentOptions,
  })

  const isAvailable = await flowComponent.isAvailable()
  if (!isAvailable) {
    throw new Error('Flow component is not available')
  }

  const containerElement = document.getElementById(containerId)

  if (containerElement.hasAttribute('data-cko-mounted')) {
    containerElement.innerHTML = ''
    containerElement.removeAttribute('data-cko-mounted')
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  const hasExistingCheckout =
    containerElement.querySelector('form, iframe, [class*="cko"], [id*="cko"]') !== null

  if (hasExistingCheckout) {
    containerElement.innerHTML = ''
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  flowComponent.mount(`#${containerId}`)
  containerElement.setAttribute('data-cko-mounted', 'true')

  let addressComponent

  if (createAddressComponent && addressContainerId) {
    try {
      await waitForCheckoutContainer(addressContainerId)

      try {
        addressComponent = checkout.create('billing_address', {})
      } catch {
        addressComponent = checkout.create('shipping_address', {})
      }

      const addressIsAvailable = await addressComponent.isAvailable()
      if (addressIsAvailable) {
        const addressContainerElement = document.getElementById(addressContainerId)
        if (addressContainerElement) {
          addressComponent.mount(`#${addressContainerId}`)
        } else {
          addressComponent = undefined
        }
      } else {
        addressComponent = undefined
      }
    } catch (error) {
      console.warn('Failed to create address component:', error)
    }
  }

  return {
    checkout,
    flowComponent,
    ...(addressComponent && { addressComponent }),
  }
}
